"use client";

import { useEffect, useState } from "react";
import { cn } from "../../lib";
import { Particles } from "../shadcn/particles";
import type { MediaPlayerTheme } from "./media-player-theme";

/** Default density for media player bar particle fields (text + audio). */
export const MEDIA_PLAYER_TRACK_PARTICLE_QUANTITY = 56;

interface MediaPlayerTrackParticlesProps {
	theme: MediaPlayerTheme;
	className?: string;
	quantity?: number;
	size?: number;
}

/** Full-bleed particles for a media player bar (not clipped by progress mask). */
export function MediaPlayerTrackParticles({
	theme,
	className,
	quantity: quantityProp,
	size = 0.6,
}: MediaPlayerTrackParticlesProps) {
	const [reduceMotion, setReduceMotion] = useState(false);

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduceMotion(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	const quantity = reduceMotion
		? 0
		: Math.max(
				0,
				Math.floor(quantityProp ?? MEDIA_PLAYER_TRACK_PARTICLE_QUANTITY),
			);

	return (
		<div
			aria-hidden
			className={cn(
				"pointer-events-none absolute inset-0 z-0 overflow-visible",
				className,
			)}
		>
			<Particles
				className="relative size-full overflow-visible"
				quantity={quantity}
				staticity={40}
				ease={70}
				color={theme.ambientParticleColor}
				size={size}
				refresh={reduceMotion}
			/>
		</div>
	);
}
